import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  LinearProgress,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import {
  People as PeopleIcon,
  EventAvailable as EventIcon,
  School as SchoolIcon,
  LocalHospital as HospitalIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

const RRHHDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const quickStats = [
    { title: 'Empleados Activos', value: '284', icon: <PeopleIcon />, color: theme.palette.primary.main, path: '/rrhh/empleados' },
    { title: 'En Licencia', value: '12', icon: <EventIcon />, color: theme.palette.info.main, path: '/rrhh/licencias' },
    { title: 'Capacitaciones Vencidas', value: '8', icon: <SchoolIcon />, color: theme.palette.warning.main, path: '/rrhh/capacitaciones' },
    { title: 'Aptos Vencidos', value: '3', icon: <HospitalIcon />, color: theme.palette.error.main, path: '/rrhh/control-medico' },
  ];

  const turnosData = [
    { turno: '7x7', cantidad: 85 },
    { turno: '14x7', cantidad: 120 },
    { turno: '21x7', cantidad: 45 },
    { turno: 'Rotativo', cantidad: 34 },
  ];

  const capacitacionesVencer = [
    { nombre: 'Juan Rodríguez', capacitacion: 'H2S', vence: '5 días' },
    { nombre: 'Pedro García', capacitacion: 'Altura', vence: '7 días' },
    { nombre: 'María López', capacitacion: 'Espacios Confinados', vence: '10 días' },
    { nombre: 'Carlos Mendoza', capacitacion: 'H2S', vence: '12 días' },
  ];

  const aptosVencidos = [
    { nombre: 'Roberto Sánchez', legajo: '1234', vencido: '15/01/2026' },
    { nombre: 'Ana Martínez', legajo: '1456', vencido: '20/01/2026' },
    { nombre: 'Diego Fernández', legajo: '1789', vencido: '28/01/2026' },
  ];

  const distribucionArea = [
    { name: 'Campo', value: 180, color: theme.palette.primary.main },
    { name: 'Administración', value: 45, color: theme.palette.secondary.main },
    { name: 'Logística', value: 35, color: theme.palette.success.main },
    { name: 'Mantenimiento', value: 24, color: theme.palette.info.main },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Recursos Humanos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión integral de personal petrolero
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PeopleIcon />} onClick={() => navigate('/rrhh/empleados')}>
          Nuevo Empleado
        </Button>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {quickStats.map((stat, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 25px ${alpha(stat.color, 0.25)}`,
                },
              }}
              onClick={() => navigate(stat.path)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h3" fontWeight={700} color={stat.color}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha(stat.color, 0.1), color: stat.color }}>
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Turnos */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Distribución por Turnos
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={turnosData}>
                  <XAxis dataKey="turno" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="cantidad" fill={theme.palette.primary.main} radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Distribución por Área */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Personal por Área
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie
                      data={distribucionArea}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="value"
                    >
                      {distribucionArea.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ pl: 2 }}>
                  {distribucionArea.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: item.color }} />
                      <Typography variant="body2">{item.name}: {item.value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Capacitaciones por Vencer */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Capacitaciones por Vencer
                </Typography>
                <Chip label="Próximos 15 días" size="small" color="warning" variant="outlined" />
              </Box>
              <List disablePadding>
                {capacitacionesVencer.map((item, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: alpha(theme.palette.warning.main, 0.05),
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                        <SchoolIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={item.nombre}
                      secondary={`${item.capacitacion} - Vence en ${item.vence}`}
                    />
                    <Chip label={item.vence} size="small" color="warning" />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Aptos Médicos Vencidos */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Aptos Médicos Vencidos
                </Typography>
                <Chip label="BLOQUEADOS" size="small" color="error" />
              </Box>
              <List disablePadding>
                {aptosVencidos.map((item, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: alpha(theme.palette.error.main, 0.05),
                      borderLeft: `4px solid ${theme.palette.error.main}`,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}>
                        <PersonOffIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={item.nombre}
                      secondary={`Legajo: ${item.legajo} - Vencido: ${item.vencido}`}
                    />
                    <Button size="small" color="error" variant="outlined">
                      Gestionar
                    </Button>
                  </ListItem>
                ))}
              </List>
              <Typography variant="caption" color="error" sx={{ mt: 2, display: 'block' }}>
                ⚠️ Personal con apto vencido no puede ser asignado a campo
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RRHHDashboard;
